import { fetchJSONWithTimeout } from '@noths/polaris-client-utils';

import { logger } from 'src/utils/serverLogger';
import type { ContentstackListingsControlsConfig } from './types/ContentstackListingsControlsConfig';

/**
 * Listings controls config resolution:
 * 1. Fresh cache — return `cachedConfig` without touching the network.
 * 2. Cache miss or expiry — fetch once, no matter how many requests are waiting
 *    on it. Concurrent callers share the same in-flight promise.
 * 3. Fetch failure — return the stale `cachedConfig` from a prior success if
 *    there is one, else `null`. Returning `null` silently drops the product's
 *    public attributes (see src/redux/product/thunks.ts), so stale beats empty.
 * 4. Recent failure — hold off before retrying, so a failing upstream does not
 *    get an attempt per request for as long as it stays down.
 */
const listingsControlsConfigUrl =
  'https://assets.contentstack.io/v3/assets/blt63f2f7afd19b09c2/blt1954bee97926b09b/sort-and-facet-configurations.json';

const CACHE_DURATION_MS = 12 * 60 * 60 * 1000; // 12 hours
const FAILURE_BACKOFF_MS = 60 * 1000; // 1 minute
const CLIENT_TIMEOUT_MS = 750;

let cachedConfig: ContentstackListingsControlsConfig | null = null;
let cacheTimestamp: number | null = null;
let lastFailureTimestamp: number | null = null;
let inFlightFetch: Promise<ContentstackListingsControlsConfig | null> | null = null;

const isCacheValid = () => {
  if (cachedConfig === null || cacheTimestamp === null) {
    return false;
  }
  return Date.now() - cacheTimestamp < CACHE_DURATION_MS;
};

// Tracked separately from cacheTimestamp so the two meanings stay distinct:
// cacheTimestamp is when we last succeeded, this is when we last failed.
// Reusing cacheTimestamp here would suppress retries for the full 12 hours.
const isWithinFailureBackoff = () =>
  lastFailureTimestamp !== null && Date.now() - lastFailureTimestamp < FAILURE_BACKOFF_MS;

const fetchAndCacheConfig = async (): Promise<ContentstackListingsControlsConfig | null> => {
  try {
    const response = await fetchJSONWithTimeout(listingsControlsConfigUrl, {
      timeout: CLIENT_TIMEOUT_MS,
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch listings controls config: ${response.statusText}`);
    }

    cachedConfig = await response.json();
    cacheTimestamp = Date.now();
    lastFailureTimestamp = null;
    return cachedConfig;
  } catch (error) {
    lastFailureTimestamp = Date.now();

    logger.error(
      `Error fetching listings controls config: ${
        error instanceof Error ? error.message : String(error)
      }`,
    );

    if (cachedConfig !== null) {
      logger.warn('Serving stale listings controls config from cache after fetch failure');
      return cachedConfig;
    }

    return null;
  }
};

export const fetchListingsControlsConfig = async () => {
  if (isCacheValid()) {
    return cachedConfig;
  }

  // Hold off after a failure. Otherwise an expired cache plus a failing
  // upstream means every request starts another attempt, back to back, with an
  // error log each time. Covers the cold-cache case too, where there is no
  // stale value to fall back on. Returning cachedConfig here serves the stale
  // value when there is one and null when there is not, matching the contract
  // of the failure path itself.
  if (isWithinFailureBackoff()) {
    return cachedConfig;
  }

  // Collapse concurrent misses into one upstream request. This matters during
  // traffic spikes: every pod the autoscaler adds starts with an empty cache,
  // and without this each concurrent render on it fires its own request.
  if (!inFlightFetch) {
    inFlightFetch = fetchAndCacheConfig().finally(() => {
      inFlightFetch = null;
    });
  }

  return inFlightFetch;
};

export const resetListingsControlsConfigCache = () => {
  cachedConfig = null;
  cacheTimestamp = null;
  lastFailureTimestamp = null;
  inFlightFetch = null;
};
